import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useEmployees, useHospitals, useOperatingRooms, useSchedulesForDay, useShiftsForDay } from '../data/queries.ts';
import { colorForHospital } from './hospitalColors.ts';
import {
  dayCoverage, CoverageProvider, StaffingModel, STAFFING_MODELS, STAFFING_MODEL_LABELS,
} from './manpowerCoverage.ts';
import { loadSiteStaffingModels, saveSiteStaffingModels, staffingModelResolver } from './siteStaffingModel.ts';

/**
 * MANPOWER COVERAGE — demand vs supply for the selected day, per site.
 *
 * The arithmetic lives in manpowerCoverage.ts (pure + unit-tested); this only renders it and owns the
 * per-site staffing-model picker. Read-only against the server: the model choice is a local preference,
 * so a non-admin can explore "what if this site ran CRNA-only" without writing anything.
 *
 * SUPPLY IS LIVE BY CONSTRUCTION. It is whatever shifts exist for this date at fetch time, so an
 * approved request (which writes a Shift row) shows up on the next load of the day — there is no cache
 * and no publish step between approval and these numbers.
 *
 * Changing a dropdown RECOMPUTES, it does not refetch: the fetched day is held in state and the
 * coverage is derived, so switching models is instant and costs no network round-trip.
 */

const pill = (bg: string, fg = '#fff'): React.CSSProperties => ({
  fontSize: '0.72em', fontWeight: 800, letterSpacing: '0.02em', padding: '3px 10px', borderRadius: '999px',
  whiteSpace: 'nowrap', background: bg, color: fg, fontFamily: 'var(--font-display)',
});
const SHORT_BG = '#b91c1c';
/**
 * Covered — needed is at or under what is on shift.
 *
 * Uses --success (the solid status green), NOT --success-text. index.css:60 states the latter's contract:
 * "legible foreground on the matching *-soft background", and all three existing uses pair it with
 * background: var(--success-soft). It also flips by theme -- #b8efce (pale mint) on dark, #15803d on
 * light -- so as BARE text on the card surface it would be nearly invisible on the dark board. --success
 * is defined once in :root with no theme override, so it reads the same either way.
 */
const COVERED_FG = 'var(--success)';
/** Local minutes-of-day as HH:MM, for the "at peak" line. */
const fmtMin = (min: number): string =>
  `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

const tile: React.CSSProperties = {
  flex: '1 1 180px', minWidth: '168px', padding: '12px 14px', borderRadius: '12px',
  background: 'var(--tint-raised, rgba(127,127,127,0.06))',
  border: '1px solid var(--border-subtle, var(--text-faint))',
  display: 'flex', flexDirection: 'column', gap: '2px', textAlign: 'left',
};
const tileLabel: React.CSSProperties = {
  fontSize: '0.62em', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
  color: 'var(--text-faint)', fontFamily: 'var(--font-display)',
};
const bigNum: React.CSSProperties = {
  fontSize: '2.5em', fontWeight: 800, lineHeight: 1.05, fontFamily: 'var(--font-display)',
  fontVariantNumeric: 'tabular-nums',
};
const tileSub: React.CSSProperties = {
  fontSize: '0.72em', color: 'var(--text-faint)', fontVariantNumeric: 'tabular-nums',
};

/**
 * One scoreboard tile.
 *
 * EVERY figure is labelled. An unlabelled "7 / 6" reads as "we have 7, need 6" -- the bigger number
 * comes first, so on a day where the NEED exceeds the supply the whole meaning inverts (Braden read
 * 10/08 exactly that way: 27 rooms at 1:4 needs 7 MDs against 6 on shift, a shortfall, and the bare
 * figures looked like a surplus).
 *
 * The delta is SIGNED in both directions: +N when there are more on shift than needed, -N when short,
 * "even" when they match. Previously a surplus drew nothing at all, so there was no way to tell
 * "exactly enough" from "three spare".
 */
const ScoreTile: React.FC<{
  label: string; needed: number; have: number; peak?: number; peakMin?: number;
  active?: boolean; onClick?: () => void;
}> = ({ label, needed, have, peak, peakMin, active, onClick }) => {
  const delta = have - needed;            // + surplus, - short
  const covered = delta >= 0;
  const hero = delta === 0 ? String(needed) : (delta > 0 ? `+${delta}` : `${delta}`);
  return (
    <button
      type="button" onClick={onClick}
      aria-pressed={!!active}
      title={delta === 0
        ? `Exactly enough: need ${needed} ${label}, have ${needed} on shift`
        : delta > 0
          ? `${delta} more ${label} on shift than needed (need ${needed}, have ${have})`
          : `${-delta} FEWER ${label} on shift than needed (need ${needed}, have ${have}). Click to highlight the sites short of them.`}
      style={{
        ...tile, font: 'inherit', color: 'inherit', cursor: onClick ? 'pointer' : 'default',
        boxShadow: active ? '0 0 0 2px var(--or-blue-mid)' : 'none',
      }}
    >
      <span style={tileLabel}>{label}</span>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
        <span style={{ ...bigNum, color: covered ? COVERED_FG : SHORT_BG }}>{hero}</span>
        <span style={tileSub}>need {needed} · have {have}</span>
      </span>
      {peak !== undefined && <span style={tileSub}>{peak} at peak{peakMin !== undefined ? ` (${fmtMin(peakMin)})` : ''}</span>}
    </button>
  );
};

const cell: React.CSSProperties = { padding: '6px 10px', fontSize: '0.86em', whiteSpace: 'nowrap' };
const head: React.CSSProperties = {
  ...cell, fontSize: '0.64em', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em',
  color: 'var(--text-faint)', fontFamily: 'var(--font-display)', textAlign: 'center',
};
const totalCell: React.CSSProperties = {
  ...cell, fontWeight: 800, borderTop: '2px solid var(--border-strong, var(--text-faint))',
};

/**
 * The Total row's need-vs-have. Green/red belongs to this day comparison alone; the per-site rows are
 * plain counts.
 *
 * Both figures carry a WORD. "7 / 6 available" was ambiguous enough to invert in the reading, which is
 * exactly what happened on 10/08. The delta is signed both ways -- +N surplus, -N short, "even" when
 * they match -- so a surplus is visible instead of drawing nothing.
 */
const NeedVsHave: React.FC<{ needed: number; have: number; label: string }> = ({ needed, have, label }) => {
  const delta = have - needed;
  const covered = delta >= 0;
  const faint = { color: 'var(--text-faint)', fontWeight: 400 } as React.CSSProperties;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
      <span style={faint}>need</span>
      <span style={{ fontWeight: 800 }}>{needed}</span>
      <span style={faint}>· have</span>
      <span style={{ fontWeight: 800, color: covered ? COVERED_FG : SHORT_BG }}>{have}</span>
      <span style={pill(covered ? COVERED_FG : SHORT_BG)}
        title={delta === 0
          ? `Exactly enough ${label}`
          : delta > 0
            ? `${delta} more ${label} on shift than needed`
            : `${-delta} fewer ${label} on shift than needed`}>
        {delta === 0 ? 'even' : (delta > 0 ? `+${delta}` : `${delta}`)}
      </span>
    </span>
  );
};

/** `day` is the date the rest was fetched FOR, so the peak scan anchors cases to it (see caseWindow). */
type Raw = { day: string; rooms: any[]; cases: any[]; hospitals: any[]; providers: CoverageProvider[] };

const ManpowerCoverageCard: React.FC<{ selectedDate: string; refreshKey?: number }> = ({ selectedDate, refreshKey }) => {
  const [models, setModels] = useState<Record<string, StaffingModel>>(loadSiteStaffingModels);
  const [focus, setFocus] = useState<'crnas' | 'doctors' | null>(null);
  // All five reads come from the shared cache (data/queries.ts), keyed by date. That also retires the old
  // latest-request-wins guard: a slow response for Tuesday can only fill Tuesday's entry, so it can never
  // land under a Wednesday heading.
  const shiftsQ = useShiftsForDay(selectedDate);
  const casesQ = useSchedulesForDay(selectedDate);
  const roomsQ = useOperatingRooms();
  const hospitalsQ = useHospitals();
  const employeesQ = useEmployees();
  const all = [shiftsQ, casesQ, roomsQ, hospitalsQ, employeesQ];
  const loading = !!selectedDate && all.some(q => q.isPending);
  const failure = all.find(q => q.isError && q.data === undefined)?.error as any;
  const error = failure ? (failure?.response?.data?.error || 'Could not load coverage for this day.') : '';

  const firstRefreshKey = useRef(refreshKey);
  const { refetch: refetchShifts } = shiftsQ;
  const { refetch: refetchCases } = casesQ;
  useEffect(() => {
    if (refreshKey !== firstRefreshKey.current) { refetchShifts(); refetchCases(); }
  }, [refreshKey, refetchShifts, refetchCases]);

  const siteNames = useMemo(
    () => new Map<string, { name: string; color: string }>((hospitalsQ.data ?? []).map((h: any) => [h.hospitalId, { name: h.name, color: colorForHospital(h) }])),
    [hospitalsQ.data],
  );
  const raw: Raw | null = useMemo(() => {
    if (!shiftsQ.data || !casesQ.data || !roomsQ.data || !hospitalsQ.data || !employeesQ.data) return null;
    const empById = new Map<string, any>(employeesQ.data.map((e: any) => [e.employeeId, e]));
    // DEDUPE BY EMPLOYEE: a provider can hold several shift rows for one day (a carried-in night shift
    // plus a day shift — the same reason the board has earliestShiftByEmployee). Counted per row they
    // would inflate supply, so one person counts once. Keep the row that carries a pecking order, since
    // that is what decides pecking vs shift work.
    const byEmployee = new Map<string, CoverageProvider>();
    for (const s of shiftsQ.data) {
      const emp = empById.get(s.employeeId);
      const existing = byEmployee.get(s.employeeId);
      const hasOrder = s.order !== undefined && s.order !== null;
      if (existing && !(hasOrder && (existing.order === undefined || existing.order === null))) continue;
      byEmployee.set(s.employeeId, {
        employeeId: s.employeeId,
        employeeType: s.employeeType || emp?.employeeType || '',
        credentials: emp?.credentials,
        order: s.order,
      });
    }
    return {
      day: selectedDate,
      rooms: roomsQ.data,
      cases: casesQ.data,
      hospitals: hospitalsQ.data,
      providers: [...byEmployee.values()],
    };
  }, [selectedDate, shiftsQ.data, casesQ.data, roomsQ.data, hospitalsQ.data, employeesQ.data]);

  // Derived, not fetched — a dropdown change recomputes without touching the network.
  const cov = useMemo(
    () => (raw ? dayCoverage(raw.rooms, raw.cases, raw.hospitals, raw.providers, staffingModelResolver(models), raw.day) : null),
    [raw, models],
  );

  const setModel = (hospitalId: string, model: StaffingModel) => {
    setModels(prev => {
      const next = { ...prev, [hospitalId]: model };
      saveSiteStaffingModels(next);
      return next;
    });
  };

  const nameOf = (hid: string) => siteNames.get(hid)?.name || hid;
  const colorOf = (hid: string) => siteNames.get(hid)?.color || 'var(--or-blue-mid)';

  // When a credential restriction bites, the plain headcount in the total row is OPTIMISTIC — somebody
  // on shift is not credentialed anywhere there is demand. Say so rather than hiding it.
  const credentialNote = cov && (cov.crnaShort > Math.max(0, cov.crnasNeeded - cov.crnasOnShift)
    || cov.doctorShort > Math.max(0, cov.doctorsNeeded - cov.doctorsOnShift));

  return (
    <div className="card" style={{ marginBottom: '16px' }}>
      <h3 style={{ margin: 0 }}>Coverage</h3>

      {loading && <div style={{ marginTop: '10px', color: 'var(--text-muted)' }}>Loading…</div>}
      {error && <div style={{ marginTop: '10px', color: '#dc2626' }}>{error}</div>}

      {!loading && !error && cov && cov.sites.length === 0 && (
        <div style={{ marginTop: '10px', color: 'var(--text-muted)', fontSize: '0.9em' }}>
          No cases scheduled on {selectedDate}, so there is nothing to staff yet.
        </div>
      )}

      {!loading && !error && cov && cov.sites.length > 0 && (
        <>
          {/* SCOREBOARD. Tiles hold their position and size in every state -- only colour and the hero
              figure change -- so the eye learns where to look. Digits are tabular so they do not jitter
              when a staffing-model dropdown changes the numbers. */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '12px' }}>
            <div style={tile}>
              <span style={tileLabel}>ORs running</span>
              <span style={{ ...bigNum, color: 'var(--or-blue-mid)' }}>
                {cov.sites.reduce((a, s) => a + s.roomsOpen, 0)}
              </span>
              <span style={tileSub}>
                across {cov.sites.length} {cov.sites.length === 1 ? 'site' : 'sites'}
              </span>
              {cov.peak && (
                <span style={tileSub}>{cov.peak.peakRooms} at once ({fmtMin(cov.peak.peakRoomsMin)})</span>
              )}
            </div>
            <ScoreTile
              label="CRNAs" needed={cov.crnasNeeded} have={cov.crnasOnShift}
              peak={cov.peak?.crnasAtPeak} peakMin={cov.peak?.crnasAtPeakMin}
              active={focus === 'crnas'}
              onClick={() => setFocus(f => (f === 'crnas' ? null : 'crnas'))}
            />
            <ScoreTile
              label="MDs" needed={cov.doctorsNeeded} have={cov.doctorsOnShift}
              peak={cov.peak?.doctorsAtPeak} peakMin={cov.peak?.doctorsAtPeakMin}
              active={focus === 'doctors'}
              onClick={() => setFocus(f => (f === 'doctors' ? null : 'doctors'))}
            />
          </div>

          <div style={{ overflowX: 'auto', marginTop: '12px' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: '760px' }}>
              <thead>
                <tr>
                  <th style={{ ...head, textAlign: 'left' }}>Site</th>
                  <th style={head}>Rooms</th>
                  <th style={head}>Staffing model</th>
                  <th style={head}>CRNAs needed</th>
                  <th style={head}>MDs needed</th>
                </tr>
              </thead>
              <tbody>
                {cov.sites.map(s => {
                  // Clicking a tile dims the sites that are NOT short of that provider type.
                  // Keyed on NEED, not eligibility: eligibility is no longer displayed, so dimming by it
                  // would give a row no visible reason for being dimmed. Clicking CRNAs now highlights
                  // the sites that actually need CRNAs (an MD-only site needs none).
                  const dim = (focus === 'crnas' && s.crnasNeeded === 0)
                    || (focus === 'doctors' && s.doctorsNeeded === 0);
                  return (
                  <tr key={s.hospitalId} style={{ opacity: dim ? 0.4 : 1 }}>
                    <td style={{ ...cell, borderLeft: `3px solid ${colorOf(s.hospitalId)}`, fontWeight: 600 }}>
                      {nameOf(s.hospitalId)}
                    </td>
                    <td style={{ ...cell, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                      {s.roomsOpen}
                      {s.peakRooms !== undefined && s.peakRooms !== s.roomsOpen && (
                        <span style={{ color: 'var(--text-faint)', fontSize: '0.84em' }}> ({s.peakRooms} pk)</span>
                      )}
                    </td>
                    <td style={{ ...cell, textAlign: 'center' }}>
                      <select
                        value={s.model}
                        aria-label={`Staffing model for ${nameOf(s.hospitalId)}`}
                        onChange={e => setModel(s.hospitalId, e.target.value as StaffingModel)}
                        style={{
                          font: 'inherit', fontSize: '0.92em', padding: '2px 6px', borderRadius: '6px',
                          border: '1px solid var(--border-subtle, var(--text-faint))',
                          background: 'var(--bg-surface)', color: 'var(--text-primary, inherit)',
                        }}
                      >
                        {STAFFING_MODELS.map(m => (
                          <option key={m} value={m}>
                            {m === 'act' ? `${STAFFING_MODEL_LABELS[m]} (1:${s.cap})` : STAFFING_MODEL_LABELS[m]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={{ ...cell, textAlign: 'center', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {s.crnasNeeded}
                    </td>
                    <td style={{ ...cell, textAlign: 'center', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {s.doctorsNeeded}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ ...totalCell, textAlign: 'left' }}>Total</td>
                  <td style={{ ...totalCell, textAlign: 'center' }}>
                    {cov.sites.reduce((a, s) => a + s.roomsOpen, 0)}
                  </td>
                  <td style={{ ...totalCell, textAlign: 'center', color: 'var(--text-faint)', fontWeight: 400 }}>—</td>
                  <td style={{ ...totalCell, textAlign: 'center' }}>
                    <NeedVsHave needed={cov.crnasNeeded} have={cov.crnasOnShift} label="CRNAs" />
                  </td>
                  <td style={{ ...totalCell, textAlign: 'center' }}>
                    <NeedVsHave needed={cov.doctorsNeeded} have={cov.doctorsOnShift} label="MDs" />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div style={{ marginTop: '8px', fontSize: '0.72em', color: 'var(--text-faint)' }}>
"pk" is peak CONCURRENT rooms; the plain figure counts every room that runs at some point
            today. The Total row compares the day's need against distinct people on shift.
          </div>

          {credentialNote && (
            <div style={{ marginTop: '8px', fontSize: '0.72em', color: 'var(--text-faint)' }}>
              Heads up: the totals count everyone on shift, but credentials mean not everyone can go
              everywhere there is demand. Allowing for that, the real gap is{' '}
              {cov.crnaShort} CRNAs and {cov.doctorShort} MDs.
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ManpowerCoverageCard;
